/**
 * Pemetaan status domain ke label bahasa Indonesia dan nada warna lencana,
 * mengikuti tabel "Status pembayaran" di DESIGN.md. Warna tidak pernah menjadi
 * satu-satunya penanda: setiap lencana selalu memuat teks.
 */

import type {
  DeliveryStatus,
  PaymentMethod,
  PaymentStatus,
  Role,
  RowStatus,
  UserStatus,
} from './apiTypes'

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'review' | 'solid'

const paymentStatusLabels: Record<PaymentStatus, string> = {
  PENDING: 'Menunggu',
  PAID: 'Lunas',
  EXPIRED: 'Kedaluwarsa',
  FAILED: 'Gagal',
  REFUNDED: 'Dikembalikan',
  CREATING: 'Menyiapkan',
}

const paymentStatusTones: Record<PaymentStatus, BadgeTone> = {
  PENDING: 'warning',
  CREATING: 'warning',
  PAID: 'success',
  EXPIRED: 'neutral',
  FAILED: 'danger',
  REFUNDED: 'info',
}

/** Label bahasa Indonesia untuk status payment. */
export function paymentStatusLabel(status: PaymentStatus): string {
  return paymentStatusLabels[status] ?? status
}

/** Nada lencana untuk status payment. */
export function paymentStatusTone(status: PaymentStatus): BadgeTone {
  return paymentStatusTones[status] ?? 'neutral'
}

const paymentMethodLabels: Record<PaymentMethod, string> = {
  qris: 'QRIS MPM',
  va: 'Virtual Account',
}

/**
 * Label kanal pembayaran. Kanal tidak menentukan warna apa pun; ia hanya
 * menjelaskan nilai mana yang harus dibaca operator (QR atau nomor VA).
 */
export function paymentMethodLabel(method: string): string {
  return paymentMethodLabels[method as PaymentMethod] ?? method
}

const deliveryStatusLabels: Record<DeliveryStatus, string> = {
  PENDING: 'Menunggu kirim',
  DELIVERED: 'Terkirim',
  DEAD: 'Gagal kirim',
}

const deliveryStatusTones: Record<DeliveryStatus, BadgeTone> = {
  PENDING: 'warning',
  DELIVERED: 'success',
  DEAD: 'danger',
}

export function deliveryStatusLabel(status: DeliveryStatus): string {
  return deliveryStatusLabels[status] ?? status
}

export function deliveryStatusTone(status: DeliveryStatus): BadgeTone {
  return deliveryStatusTones[status] ?? 'neutral'
}

const rowStatusLabels: Record<RowStatus, string> = {
  ACTIVE: 'Aktif',
  DISABLED: 'Nonaktif',
}

export function rowStatusLabel(status: RowStatus | UserStatus): string {
  return rowStatusLabels[status as RowStatus] ?? status
}

export function rowStatusTone(status: RowStatus | UserStatus): BadgeTone {
  return status === 'ACTIVE' ? 'success' : 'neutral'
}

const roleLabels: Record<Role, string> = {
  admin: 'Admin',
  viewer: 'Viewer',
}

export function roleLabel(role: Role): string {
  return roleLabels[role] ?? role
}

const environmentLabels: Record<string, string> = {
  development: 'Lingkungan pengembangan',
  staging: 'Lingkungan staging',
  production: 'Lingkungan produksi',
}

/** Label panjang untuk lencana environment di kepala halaman. */
export function environmentLabel(environment: string): string {
  return environmentLabels[environment] ?? `Lingkungan ${environment}`
}

export function environmentTone(environment: string): BadgeTone {
  if (environment === 'production') return 'danger'
  if (environment === 'staging') return 'warning'
  return 'info'
}

const verificationLabels: Record<string, string> = {
  ok: 'Terverifikasi',
  failed: 'Gagal verifikasi',
  log_only_bypass: 'Dilewati (mode log saja)',
}

export function verificationLabel(verification: string): string {
  return verificationLabels[verification] ?? verification
}

const eventTypeLabels: Record<string, string> = {
  created: 'Payment dibuat',
  paid: 'Payment lunas',
  expired: 'Payment kedaluwarsa',
  failed: 'Payment gagal',
  refunded: 'Payment dikembalikan',
  review_flagged: 'Ditandai perlu tinjauan',
  review_resolved: 'Tinjauan diselesaikan',
  status_refreshed: 'Status dicek ke bank',
  simulated: 'Disimulasikan di konsol',
}

export function eventTypeLabel(type: string): string {
  return eventTypeLabels[type] ?? type
}
